import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../App";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, BookOpen, Lightbulb, Brain, Check, X, Loader, Clock } from "lucide-react";

export default function Practice() {
  const { examType, discipline } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ correct: 0, total: 0 });

  useEffect(() => {
    fetchQuestions();
  }, [examType, discipline]);

  const fetchQuestions = async () => {
    try {
      const res = await api.get("/questions", {
        params: { exam_type: examType, discipline, limit: 100 }
      });
      setQuestions(res.data);
    } catch (err) {
      console.error("Error fetching questions:", err);
      toast.error("Failed to load questions");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = async (answer) => {
    if (selectedAnswer) return;
    setSelectedAnswer(answer);
    
    const question = questions[currentIndex];
    const isCorrect = answer === question.correct_answer;
    
    setStats(prev => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      total: prev.total + 1
    }));
    
    setShowExplanation(true);

    try {
      await api.post(`/questions/answer?question_id=${question.question_id}&answer=${answer}`);
    } catch (err) {
      // Ignore progress save errors
    }
  };

  const nextQuestion = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  };

  const prevQuestion = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-slate-600">Loading 100 questions...</p>
        </div>
      </div>
    );
  }

  const question = questions[currentIndex];
  const accuracy = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                data-testid="back-btn"
                onClick={() => navigate(`/disciplines/${examType}`)}
                className="btn-ghost p-2"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-lg font-bold text-slate-900">Practice</h1>
                <p className="text-sm text-slate-500">{discipline.replace(/_/g, " ")}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-slate-900">{stats.correct}/{stats.total}</p>
                <p className="text-xs text-slate-500">{accuracy}% accuracy</p>
              </div>
              <button
                data-testid="mock-exam-btn"
                onClick={() => navigate(`/mock-exam/${examType}/${discipline}`)}
                className="btn-secondary flex items-center gap-2 text-sm"
              >
                <Clock size={16} />
                Mock Exam
              </button>
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-4 progress-bar">
            <div
              className="progress-bar-fill"
              style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
            />
          </div>
          <p className="text-xs text-slate-500 mt-2">Question {currentIndex + 1} of {questions.length}</p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {question ? (
          <div className="animate-fade-in">
            {/* Question */}
            <div className="question-card mb-6">
              <div className="flex items-start gap-3 mb-4">
                <span className="px-2 py-1 rounded-md bg-blue-100 text-blue-700 text-xs font-medium">
                  {question.topic}
                </span>
                <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                  question.difficulty === "easy" ? "bg-green-100 text-green-700" :
                  question.difficulty === "hard" ? "bg-red-100 text-red-700" :
                  "bg-yellow-100 text-yellow-700"
                }`}>
                  {question.difficulty}
                </span>
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-6">
                {question.question_text}
              </h2>

              {/* Options */}
              <div className="space-y-3">
                {Object.entries(question.options).map(([key, value]) => {
                  const isSelected = selectedAnswer === key;
                  const isCorrect = key === question.correct_answer;
                  let btnClass = "option-btn";
                  
                  if (selectedAnswer) {
                    if (isCorrect) btnClass += " correct";
                    else if (isSelected) btnClass += " incorrect";
                  }
                  if (isSelected && !showExplanation) btnClass += " selected";

                  return (
                    <button
                      key={key}
                      data-testid={`option-${key}`}
                      onClick={() => handleAnswer(key)}
                      disabled={!!selectedAnswer}
                      className={btnClass}
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center font-semibold text-slate-700">
                          {key}
                        </span>
                        <span className="flex-1">{value}</span>
                        {selectedAnswer && isCorrect && <Check className="w-5 h-5 text-green-600" />}
                        {selectedAnswer && isSelected && !isCorrect && <X className="w-5 h-5 text-red-600" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Explanation */}
            {showExplanation && (
              <div className="space-y-4 animate-fade-in">
                {/* Main explanation */}
                <div className="card border-l-4 border-blue-500">
                  <div className="flex items-start gap-3">
                    <BookOpen className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">Explanation</h3>
                      <p className="text-slate-700">{question.explanation}</p>
                    </div>
                  </div>
                </div>

                {/* Handbook reference */}
                <div className="card border-l-4 border-green-500">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">Handbook Reference</h3>
                      <p className="text-slate-700">{question.handbook_reference}</p>
                    </div>
                  </div>
                </div>

                {/* Memory tip */}
                <div className="card border-l-4 border-purple-500">
                  <div className="flex items-start gap-3">
                    <Brain className="w-5 h-5 text-purple-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">Memory Tip</h3>
                      <p className="text-slate-700">{question.memory_tip}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between items-center mt-8">
              <button
                data-testid="prev-btn"
                onClick={prevQuestion}
                disabled={currentIndex === 0}
                className="btn-secondary flex items-center gap-2 disabled:opacity-50"
              >
                <ArrowLeft size={18} />
                Previous
              </button>
              
              {showExplanation ? (
                <button
                  data-testid="next-btn"
                  onClick={nextQuestion}
                  disabled={currentIndex === questions.length - 1}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50"
                >
                  Next Question
                  <ArrowRight size={18} />
                </button>
              ) : (
                <p className="text-slate-500 text-sm">Select an answer to continue</p>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-slate-600">No questions available</p>
          </div>
        )}
      </main>
    </div>
  );
}
